import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { APP_NAME, BLAZE_NAME, giftLength, type BlazeGiftPreviewDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { blazeApi } from '@/api/endpoints';
import { Wordmark } from '@/components/Brand';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Fire, Gift } from '@/components/ui/icons';
import { Spinner } from '@/components/ui/Spinner';
import { useBlaze } from '@/stores/blaze';
import { toast, useUi } from '@/stores/ui';

/** A gift link (/gift/CODE): who it's from, and redeeming it. */
export function GiftPage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const [gift, setGift] = useState<BlazeGiftPreviewDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    let cancelled = false;
    blazeApi
      .gift(code)
      .then((g) => !cancelled && setGift(g))
      .catch((err) => !cancelled && setError(errorMessage(err)));
    return () => {
      cancelled = true;
    };
  }, [code]);

  const redeem = async () => {
    setRedeeming(true);
    try {
      useBlaze.getState().set(await blazeApi.redeem(code));
      toast.success(`Gift redeemed. Enjoy ${BLAZE_NAME}!`);
      navigate('/app/@me');
      useUi.getState().openModal({ type: 'userSettings', section: 'blaze' });
    } catch (err) {
      setError(errorMessage(err));
      setRedeeming(false);
    }
  };

  const length = gift ? giftLength(gift.months) : '';
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-sidebar px-4">
      <Wordmark />
      <div className="w-full max-w-[440px] animate-slide-up overflow-hidden rounded-2xl border border-line bg-surface text-center shadow-pop">
        <div className="relative flex h-28 items-center justify-center bg-gradient-to-br from-[#f97316] via-[#e11d48] to-[#7c3aed] text-white">
          <Gift size={52} aria-hidden />
          <Fire size={90} aria-hidden className="absolute -right-3 -bottom-4 opacity-20" />
        </div>
        <div className="p-8 pt-6">
          {!gift && !error && (
            <div className="flex justify-center py-6 text-fg-muted">
              <Spinner />
            </div>
          )}
          {error && !gift && (
            <>
              <h1 className="text-xl font-bold">This gift link doesn&apos;t work</h1>
              <p className="mt-2 text-fg-muted">{error}</p>
            </>
          )}
          {gift && (
            <>
              {gift.from && (
                <p className="flex items-center justify-center gap-2 text-sm text-fg-muted">
                  <Avatar user={gift.from} size={20} /> {gift.from.displayName} sent you a gift
                </p>
              )}
              <h1 className="mt-1 text-2xl font-extrabold tracking-tight">
                {length} of {BLAZE_NAME}
              </h1>
              <p className="mt-2 text-sm text-fg-muted">Emoji and sounds from every server, server profiles, 60 fps streaming, bigger uploads and two Stokes.</p>
              {!gift.redeemable && <p className="mt-4 text-sm font-medium text-warning">This gift has already been used.</p>}
              {error && <p className="mt-4 text-sm font-medium text-danger">{error}</p>}
              {gift.redeemable && (
                <Button className="mt-6" size="lg" block loading={redeeming} onClick={redeem}>
                  Redeem gift
                </Button>
              )}
            </>
          )}
          <Link to="/app/@me" className="mt-4 inline-block text-sm text-fg-muted hover:text-fg-soft hover:underline">
            Continue to {APP_NAME}
          </Link>
        </div>
      </div>
    </div>
  );
}
